import React, { useState, useEffect } from 'react';
import { pagamentiStraordinariApi, usersApi } from '../../lib/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, 
  AlertTriangle,
  ArrowRight,
  Trash2,
  Eye,
  Receipt,
  User,
  Euro,
  Search,
  X,
  FileText,
  Calendar,
  Building2,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPI_STRAORDINARIO = [
  { value: 'imposta_registro', label: 'Quota Imposta di Registro' },
  { value: 'imposta_registro_annualita', label: 'Imposta Registro Annualità Successive' },
  { value: 'conguaglio_condominio', label: 'Conguaglio Condominio' },
  { value: 'intervento_riparazione', label: 'Intervento di Riparazione' },
  { value: 'altro', label: 'Altro (specificare)' }
];

const PagamentiStraordinariPage = () => {
  const [straordinari, setStraordinari] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form data - nuovo formato con contratto e mensilità
  const [formData, setFormData] = useState({
    tipo_straordinario: '',
    descrizione: '',
    importo: '',
    costi_operazione: '0',
    contract_id: '',
    pagatore_id: '',
    beneficiario_id: '',
    month: new Date().toISOString().slice(0, 7) // YYYY-MM
  });

  // Ricerca utenti (pagatore)
  const [pagatoreSearch, setPagatoreSearch] = useState('');
  const [pagatoreResults, setPagatoreResults] = useState([]);
  const [selectedPagatore, setSelectedPagatore] = useState(null);
  
  // Contratti del pagatore selezionato
  const [pagatoreContracts, setPagatoreContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedBeneficiario, setSelectedBeneficiario] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [straordinariRes, usersRes] = await Promise.all([
        pagamentiStraordinariApi.getAll(),
        usersApi.getAll()
      ]);
      setStraordinari(straordinariRes.data);
      // Filtra admin dalla lista utenti
      setUsers(usersRes.data.filter(u => u.ruolo !== 'admin'));
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // Ricerca utenti per pagatore
  const handlePagatoreSearch = (query) => {
    setPagatoreSearch(query);
    if (query.length < 2) {
      setPagatoreResults([]);
      return;
    }
    const results = users.filter(u => 
      `${u.nome} ${u.cognome}`.toLowerCase().includes(query.toLowerCase()) ||
      u.ragione_sociale?.toLowerCase().includes(query.toLowerCase()) ||
      u.email?.toLowerCase().includes(query.toLowerCase()) ||
      u.codice_fiscale?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 8);
    setPagatoreResults(results);
  };

  // Quando si seleziona il pagatore, carica i suoi contratti
  const selectPagatore = async (user) => {
    setSelectedPagatore(user);
    setFormData({...formData, pagatore_id: user.id, contract_id: '', beneficiario_id: ''});
    setPagatoreSearch('');
    setPagatoreResults([]);
    setSelectedContract(null);
    setSelectedBeneficiario(null);
    
    // Carica contratti dove l'utente è inquilino o garante
    try {
      const res = await axios.get(`${API_URL}/users/${user.id}/contracts`);
      setPagatoreContracts(res.data);
      if (res.data.length === 0) {
        toast.info('Nessun contratto attivo trovato per questo utente');
      }
    } catch (error) {
      toast.error('Errore nel caricamento contratti');
      setPagatoreContracts([]);
    }
  };

  // Quando si seleziona il contratto, imposta automaticamente il beneficiario (controparte)
  const selectContract = (contract) => {
    setSelectedContract(contract);

    // La controparte (beneficiario) è calcolata lato backend in base al ruolo del pagatore:
    // pagatore inquilino -> beneficiario proprietario; pagatore proprietario -> beneficiario inquilino
    const beneficiari = contract.beneficiari || [];
    if (beneficiari.length > 0) {
      const ben = beneficiari[0];
      setSelectedBeneficiario(ben);
      setFormData({
        ...formData,
        contract_id: contract.id,
        beneficiario_id: ben.id
      });
    } else {
      setSelectedBeneficiario(null);
      setFormData({...formData, contract_id: contract.id, beneficiario_id: ''});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipo_straordinario) {
      toast.error('Seleziona il tipo di pagamento');
      return;
    }
    if (!formData.importo || parseFloat(formData.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (formData.tipo_straordinario === 'altro' && !formData.descrizione) {
      toast.error('Descrizione obbligatoria per tipo "Altro"');
      return;
    }
    if (!formData.pagatore_id) {
      toast.error('Seleziona chi paga');
      return;
    }
    if (!formData.contract_id) {
      toast.error('Seleziona il contratto di riferimento');
      return;
    }
    if (!formData.beneficiario_id) {
      toast.error('Seleziona chi riceve');
      return;
    }
    if (!formData.month) {
      toast.error('Seleziona la mensilità');
      return;
    }
    if (formData.pagatore_id === formData.beneficiario_id) {
      toast.error('Pagatore e beneficiario devono essere diversi');
      return;
    }

    try {
      const payload = {
        ...formData,
        importo: parseFloat(formData.importo),
        costi_operazione: parseFloat(formData.costi_operazione) || 0
      };

      await pagamentiStraordinariApi.create(payload);
      toast.success('Pagamento straordinario creato! SDD e SEPA generati.');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo pagamento straordinario e i relativi SDD/SEPA?')) return;
    
    try {
      await pagamentiStraordinariApi.delete(id);
      toast.success('Pagamento eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleViewDetail = async (item) => {
    try {
      const response = await pagamentiStraordinariApi.getById(item.id);
      setSelectedItem(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_straordinario: '',
      descrizione: '',
      importo: '',
      costi_operazione: '0',
      contract_id: '',
      pagatore_id: '',
      beneficiario_id: '',
      month: new Date().toISOString().slice(0, 7)
    });
    setSelectedPagatore(null);
    setSelectedContract(null);
    setSelectedBeneficiario(null);
    setPagatoreContracts([]);
    setPagatoreSearch('');
  };

  const getUserName = (user) => {
    if (!user) return '-';
    if (user.is_societa && user.ragione_sociale) return user.ragione_sociale;
    return `${user.nome || ''} ${user.cognome || ''}`.trim() || '-';
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '-';
    const [year, month] = monthStr.split('-');
    const mesi = ['', 'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${mesi[parseInt(month)]} ${year}`;
  };

  const getPaymentStatusBadge = (item) => {
    const payments = item.payments_summary || [];
    if (payments.length === 0) return null;
    
    const statuses = payments.map(p => p.status);
    if (statuses.every(s => s === 'paid' || s === 'completed')) {
      return <Badge className="bg-green-100 text-green-800">Concluso</Badge>;
    }
    if (statuses.some(s => s === 'open')) {
      return <Badge className="bg-blue-100 text-blue-800">In distinta</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800">Pending</Badge>;
  };

  const renderStraordinariTable = (items, showActions) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo</TableHead>
          <TableHead>Contratto</TableHead>
          <TableHead>Mensilità</TableHead>
          <TableHead>Pagatore</TableHead>
          <TableHead>Beneficiario</TableHead>
          <TableHead className="text-center">Stato</TableHead>
          <TableHead className="text-right">Importo</TableHead>
          <TableHead className="text-right">Azioni</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="text-center py-8 text-slate-500">
              {showActions ? 'Nessun pagamento straordinario corrente' : 'Nessun pagamento straordinario evaso'}
            </TableCell>
          </TableRow>
        ) : (
          items.map((item) => (
            <TableRow key={item.id} data-testid={`straordinario-row-${item.id}`}>
              <TableCell>
                <Badge className="bg-orange-100 text-orange-800">
                  {item.tipo_label || item.tipo_straordinario}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="font-mono text-sm">{item.codice_contratto || '-'}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">{formatMonth(item.month)}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-red-400" />
                  <span className="text-sm">{item.pagatore_nome}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-green-400" />
                  <span className="text-sm">{item.beneficiario_nome}</span>
                </div>
              </TableCell>
              <TableCell className="text-center">
                {getPaymentStatusBadge(item)}
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold">{item.importo?.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDetail(item)}
                    data-testid={`view-${item.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  {showActions && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="p-6 space-y-6" data-testid="pagamenti-straordinari-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pagamenti Straordinari</h1>
          <p className="text-slate-500">Gestisci pagamenti extra-canone collegati ai contratti</p>
        </div>
        <Button 
          onClick={() => setShowDialog(true)}
          data-testid="new-straordinario-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Straordinario
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Come funziona:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Seleziona chi paga (inquilino/garante o proprietario), poi scegli il contratto di riferimento</li>
                <li>Il beneficiario (la controparte) viene impostato automaticamente: se paga l'inquilino riceve il proprietario, se paga il proprietario riceve l'inquilino</li>
                <li>Il pagamento apparirà nella cronologia del contratto</li>
                <li>La descrizione verrà riportata nella distinta XML</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Straordinari con Tab Correnti/Evasi */}
      <Tabs defaultValue="correnti" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="correnti" className="flex items-center gap-2" data-testid="tab-correnti">
            <Clock className="w-4 h-4" />
            Correnti ({straordinari.filter(s => s.stato !== 'evaso').length})
          </TabsTrigger>
          <TabsTrigger value="evasi" className="flex items-center gap-2" data-testid="tab-evasi">
            <CheckCircle2 className="w-4 h-4" />
            Evasi ({straordinari.filter(s => s.stato === 'evaso').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="correnti">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Pagamenti Straordinari Correnti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderStraordinariTable(straordinari.filter(s => s.stato !== 'evaso'), true)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evasi">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Pagamenti Straordinari Evasi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderStraordinariTable(straordinari.filter(s => s.stato === 'evaso'), false)}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo Straordinario */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" aria-describedby="new-straordinario-desc">
          <DialogHeader>
            <DialogTitle>Nuovo Pagamento Straordinario</DialogTitle>
            <DialogDescription id="new-straordinario-desc">
              Crea un pagamento straordinario collegato a un contratto
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo */}
            <div>
              <Label>Tipo Pagamento *</Label>
              <Select
                value={formData.tipo_straordinario}
                onValueChange={(v) => setFormData({...formData, tipo_straordinario: v})}
              >
                <SelectTrigger data-testid="tipo-select">
                  <SelectValue placeholder="Seleziona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_STRAORDINARIO.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Descrizione (se tipo=altro) */}
            {formData.tipo_straordinario === 'altro' && (
              <div>
                <Label>Descrizione *</Label>
                <Input
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  placeholder="Descrizione pagamento..."
                  data-testid="descrizione-input"
                />
              </div>
            )}

            {/* Chi Paga - Ricerca Utente */}
            <div>
              <Label>Chi Paga *</Label>
              {selectedPagatore ? (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <User className="w-5 h-5 text-red-600" />
                  <div className="flex-1">
                    <p className="font-medium">{getUserName(selectedPagatore)}</p>
                    <p className="text-xs text-slate-500">{selectedPagatore.codice_fiscale}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedPagatore(null);
                      setSelectedContract(null);
                      setSelectedBeneficiario(null);
                      setPagatoreContracts([]);
                      setFormData({...formData, pagatore_id: '', contract_id: '', beneficiario_id: ''});
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center border rounded-lg">
                    <Search className="w-4 h-4 ml-3 text-slate-400" />
                    <Input
                      value={pagatoreSearch}
                      onChange={(e) => handlePagatoreSearch(e.target.value)}
                      placeholder="Cerca per nome, cognome, CF..."
                      className="border-0"
                      data-testid="pagatore-search"
                    />
                  </div>
                  {pagatoreResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                      {pagatoreResults.map(user => (
                        <div
                          key={user.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                          onClick={() => selectPagatore(user)}
                        >
                          <p className="font-medium">{getUserName(user)}</p>
                          <p className="text-xs text-slate-500">{user.codice_fiscale} • {user.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Contratto di Riferimento */}
            {selectedPagatore && (
              <div>
                <Label>Contratto di Riferimento *</Label>
                {pagatoreContracts.length === 0 ? (
                  <p className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded-lg">
                    Nessun contratto attivo per questo utente
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-auto">
                    {pagatoreContracts.map(contract => (
                      <div
                        key={contract.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedContract?.id === contract.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => selectContract(contract)}
                        data-testid={`contract-option-${contract.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="font-mono font-medium">{contract.codice_contratto}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                          <Building2 className="w-3 h-3" />
                          <span>{contract.immobile_indirizzo}, {contract.immobile_citta}</span>
                        </div>
                        {contract.beneficiario_default_nome && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-green-600">
                            <ArrowRight className="w-3 h-3" />
                            <span>
                              {contract.user_role === 'proprietario' ? 'Inquilino' : 'Proprietario'}: {contract.beneficiario_default_nome}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Chi Riceve (auto-popolato) */}
            {selectedContract && selectedBeneficiario && (
              <div>
                <Label>Chi Riceve</Label>
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <User className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium">{getUserName(selectedBeneficiario)}</p>
                    <p className="text-xs text-slate-500">
                      {selectedContract.user_role === 'proprietario' ? "Inquilino del contratto" : "Proprietario dell'immobile"}
                    </p>
                  </div>
                </div>
                {(selectedContract.beneficiari?.length > 1) && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">
                      {selectedContract.user_role === 'proprietario' ? 'Altri inquilini:' : 'Altri proprietari:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedContract.beneficiari.filter(b => b.id !== selectedBeneficiario.id).map(ben => (
                        <Badge 
                          key={ben.id} 
                          variant="outline"
                          className="cursor-pointer hover:bg-green-50"
                          onClick={() => {
                            setSelectedBeneficiario(ben);
                            setFormData({...formData, beneficiario_id: ben.id});
                          }}
                        >
                          {getUserName(ben)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Importo e Costi */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Importo *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.importo}
                    onChange={(e) => setFormData({...formData, importo: e.target.value})}
                    className="pl-9"
                    placeholder="0.00"
                    data-testid="importo-input"
                  />
                </div>
              </div>
              <div>
                <Label>Costi Operazione</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.costi_operazione}
                    onChange={(e) => setFormData({...formData, costi_operazione: e.target.value})}
                    className="pl-9"
                    placeholder="0.00"
                    data-testid="costi-input"
                  />
                </div>
              </div>
            </div>

            {/* Mensilità */}
            <div>
              <Label>Mensilità *</Label>
              <Input
                type="month"
                value={formData.month}
                onChange={(e) => setFormData({...formData, month: e.target.value})}
                data-testid="month-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Il pagamento verrà contabilizzato in questa mensilità
              </p>
            </div>

            {/* Riepilogo */}
            {formData.importo && selectedContract && (
              <div className="p-4 bg-slate-50 rounded-lg border" data-testid="straordinario-riepilogo">
                <p className="font-medium mb-2">Riepilogo:</p>
                {selectedPagatore && selectedBeneficiario && (
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <div className="flex items-center gap-1 text-red-600">
                      <User className="w-4 h-4" />
                      <span className="font-medium" data-testid="riepilogo-pagatore">{getUserName(selectedPagatore)}</span>
                      <span className="text-xs text-slate-400">
                        ({selectedContract.user_role === 'proprietario' ? 'proprietario' : 'inquilino/garante'})
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <div className="flex items-center gap-1 text-green-600">
                      <User className="w-4 h-4" />
                      <span className="font-medium" data-testid="riepilogo-beneficiario">{getUserName(selectedBeneficiario)}</span>
                      <span className="text-xs text-slate-400">
                        ({selectedContract.user_role === 'proprietario' ? 'inquilino' : 'proprietario'})
                      </span>
                    </div>
                  </div>
                )}
                <div className="text-sm space-y-1">
                  <p>
                    <span className="text-slate-500">SDD da incassare:</span>{' '}
                    <span className="font-semibold">
                      {(parseFloat(formData.importo || 0) + parseFloat(formData.costi_operazione || 0)).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €
                    </span>
                  </p>
                  <p>
                    <span className="text-slate-500">SEPA da bonificare:</span>{' '}
                    <span className="font-semibold">
                      {parseFloat(formData.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €
                    </span>
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {setShowDialog(false); resetForm();}}>
                Annulla
              </Button>
              <Button type="submit" data-testid="submit-straordinario">
                Crea Pagamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg" aria-describedby="detail-desc">
          <DialogHeader>
            <DialogTitle>Dettaglio Pagamento Straordinario</DialogTitle>
            <DialogDescription id="detail-desc">
              Informazioni complete sul pagamento
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Tipo</Label>
                  <p className="font-medium">{selectedItem.tipo_label || selectedItem.tipo_straordinario}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Contratto</Label>
                  <p className="font-mono">{selectedItem.codice_contratto || '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Mensilità</Label>
                  <p>{formatMonth(selectedItem.month)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Importo</Label>
                  <p className="font-semibold">{selectedItem.importo?.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-slate-500">Pagatore</Label>
                    <p className="font-medium">{selectedItem.pagatore_nome}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                  <div className="flex-1">
                    <Label className="text-slate-500">Beneficiario</Label>
                    <p className="font-medium">{selectedItem.beneficiario_nome}</p>
                  </div>
                </div>
              </div>

              {selectedItem.payments && selectedItem.payments.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-slate-500 block mb-2">Pagamenti Collegati</Label>
                  <div className="space-y-2">
                    {selectedItem.payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <Badge className={p.tipo === 'sdd' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                            {p.tipo.toUpperCase()}
                          </Badge>
                          <span className="text-sm">{p.status}</span>
                        </div>
                        <span className="font-mono text-sm">
                          {(p.sdd_amount || p.sepa_amount)?.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedItem.descrizione && (
                <div className="border-t pt-4">
                  <Label className="text-slate-500">Descrizione</Label>
                  <p>{selectedItem.descrizione}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDetailDialog(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagamentiStraordinariPage;
